import React from 'react';
import { Box, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { House } from '@/client';
import { ClassifiedHouse } from '@/utils/properties/propertyGroupingUtils';
import { getPropertyMeta } from '@/components/properties/cards/PropertiesCardGrid/propertyCardUtils.ts';
import { splitAddress } from '@/utils/properties/addressUtils.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import { isDraftHouse } from '@/utils/houseUtils';
import PropertySelectorLockedSubscribeCard from './PropertySelectorLockedSubscribeCard.tsx';
import PropertySelectorRowStatus from './PropertySelectorRowStatus.tsx';
import PropertySelectorSectionHeader from './PropertySelectorSectionHeader.tsx';
import {
	getPropertySelectorRowIcon,
	PROPERTY_SELECTOR_LOCKED_ROW_OPACITY,
	PropertySelectorGroupKey,
} from './propertySelectorSections.ts';
import PropertyCopyTag from '@/components/properties/PropertyCopyTag';
import PropertyNotificationMarker from '@/components/properties/PropertyNotificationMarker';

interface PropertySelectorGroupMobileProps {
	groupKey: string;
	title: string;
	houses: ClassifiedHouse[];
	lockedCount: number;
	selectedHouseId: number;
	onHouseSelect: (house: House) => void;
	isHouseLocked: (houseId: number) => boolean;
	onSubscribe?: () => void;
	isSubscribeLoading?: boolean;
	transferringPropertyIds?: Set<number>;
	unreadNotificationPropertyIds?: Set<number>;
}

const PropertySelectorGroupMobile: React.FC<PropertySelectorGroupMobileProps> = (props) => {
	const {
		groupKey,
		title,
		houses,
		lockedCount,
		selectedHouseId,
		onHouseSelect,
		isHouseLocked,
		onSubscribe,
		isSubscribeLoading,
		transferringPropertyIds,
		unreadNotificationPropertyIds,
	} = props;
	const { user } = useAuth();
	const showSubscribeCard = groupKey === PropertySelectorGroupKey.Shared && lockedCount > 0 && !!onSubscribe;

	return (
		<VStack
			gap={1.5}
			w="full"
			align="start"
			_notFirst={{ borderTopWidth: '1px', borderColor: 'border.default', pt: 4 }}
		>
			<Box px={1} pb={1.5}>
				<PropertySelectorSectionHeader title={title} count={houses.length} />
			</Box>

			<VStack gap={2} w="full">
				{houses.map((house) => {
					const { street, area } = splitAddress(house.address);
					const isTransferring = transferringPropertyIds?.has(house.id) ?? false;
					const meta = getPropertyMeta(house, user?.id, isTransferring);
					const rowIcon = getPropertySelectorRowIcon(house, user?.id);
					const isSelected = selectedHouseId === house.id;
					const isDraft = isDraftHouse(house);
					const isLocked = isHouseLocked(house.id);
					const rowBg = isSelected ? 'sidebar.active' : 'surface.subtle';
					const hasUnreadNotification = unreadNotificationPropertyIds?.has(house.id) ?? false;

					return (
						<Box
							key={house.id}
							w="full"
							borderRadius="2xl"
							borderWidth="1.5px"
							borderStyle={isDraft && !isLocked ? 'dashed' : 'solid'}
							borderColor={isDraft && !isLocked ? 'border.default' : 'transparent'}
							bg={rowBg}
							cursor={isLocked ? 'default' : 'pointer'}
							opacity={isLocked ? PROPERTY_SELECTOR_LOCKED_ROW_OPACITY : 1}
							onClick={isLocked ? undefined : () => onHouseSelect(house)}
							px={3}
							py={2}
							transition="background-color 120ms ease, border-color 160ms ease"
							_active={
								isLocked
									? undefined
									: { bg: 'surface.default', borderColor: isDraft ? 'border.strong' : 'transparent' }
							}
						>
							<HStack gap={3} w="full" align="center" minW={0}>
								<Icon
									as={rowIcon}
									boxSize={4}
									color={isSelected ? 'fg' : 'fg.muted'}
									opacity={isSelected ? 1 : 0.7}
									flexShrink={0}
								/>
								<VStack gap={0} align="start" flex={1} justify="center" minW={0}>
									<HStack gap={1} minW={0} maxW="full">
										<Text
											fontSize="md"
											fontWeight={isSelected ? 'medium' : 'normal'}
											truncate
											minW={0}
										>
											{street}
										</Text>
										<PropertyCopyTag isCopy={house.isCopy} />
										<PropertyNotificationMarker hasUnreadNotification={hasUnreadNotification} />
									</HStack>
									{area && (
										<Text color="fg.muted" fontSize="sm" truncate maxW="full">
											{area}
										</Text>
									)}
								</VStack>
								<PropertySelectorRowStatus
									isLocked={isLocked}
									badge={meta.badge}
									colorPalette={meta.colorPalette}
								/>
							</HStack>
						</Box>
					);
				})}
			</VStack>

			{showSubscribeCard && (
				<PropertySelectorLockedSubscribeCard
					lockedCount={lockedCount}
					onSubscribe={onSubscribe}
					isLoading={isSubscribeLoading}
				/>
			)}
		</VStack>
	);
};

export default PropertySelectorGroupMobile;
